import React from "react";
import "../common/LicenseDetail.css";
import { useState, useRef, useEffect } from "react";
import Icon from "../../../common/Icon";
import cancelButton from "./../../../../assets/img/icon/cancleButton.svg";
import downloadButton from "./../../../../assets/img/icon/downloadButton.svg";
import cloudUpload from "./../../../../assets/img/icon/cloudUpload.svg";
import editIcon from "./../../../../assets/img/icon/editIcon.svg";
import EditDetails from "../common/EditDetails";
import CostDetailsAccordion from "../common/CostDetailsAccordion";
import { CostEditFormData } from "../common/CostDetailsEditForm";
import { useUpdateLicenseApplication } from "../../../../backend/license";
import { hasUserAccess, getUserDetails, getUserRole, } from "../../../../backend/auth";
import { toast } from "react-toastify";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import PageLoader from "../../../shared/PageLoader";
import {
  useGetLicenseApplicationDocuments,
  useGetApplicationTypeDocuments,
  useGetLicenseApplicationHistory,
  useGetLicenseApplication,
  useCreateLicenseApplicationDocument,
} from "../../../../backend/license";
import { useQueryClient } from "@tanstack/react-query";
import { downloadS3FileSecurely, formatDateDisplay, parseS3Url } from "../../../../utils/common";
import { post } from "../../../../backend/request";

interface LicenseDetailProps {
  isOpen: boolean;
  closePanel: () => void;
  license?: any;
  refetch?: () => void;
}

interface HistoryChange {
  from: any;
  to: any;
}

interface HistoryItem {
  id: number;
  action: string;
  changed_by: string;
  changed_at: string;
  changes: Record<string, HistoryChange>;
}

interface DisplayHistoryItem {
  id: number;
  title: string;
  date: string;
  rawDate: string;
  description: string;
  name: string;
}

type PopupData = {
  title: string;
  description: string;
  uploadBoxText?: string;
};

interface UploadedFile {
  file: File;
  url: string;
  uploaded_url?: string;
  uploading?: boolean;
  file_name?: string;
  content_type?: string;
  file_size?: number;
  s3_key?: string;
  s3_bucket?: string;
  s3_region?: string;
}

const LicenseApplicationDetail: React.FC<LicenseDetailProps> = ({
  isOpen,
  closePanel,
  license,
  refetch,
}) => {
  const [activeTab, setActiveTab] = useState<
    "details" | "history" | "documents"
  >("details");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [open, setIsOpen] = useState(false);
  const [popupData, setPopupData] = useState<PopupData | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("not_started");
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const [isPerpetual, setIsPerpetual] = useState<boolean>(false);
  const [issuedDate, setIssuedDate] = useState<string>("");
  const [expiryDate, setExpiryDate] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [lastUpdatedStatus, setLastUpdatedStatus] = useState<string | null>(null);
  const [allMandatoryDocsSubmitted, setAllMandatoryDocsSubmitted] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const user = getUserDetails();
  const queryClient = useQueryClient();

  const { application: fetchedApplication, isFetching: isFetchingApplication } = useGetLicenseApplication(
    license && license.id ? license.id : "",
    !!(license && license.id && isOpen)
  );

  // Use fetched data when available, fallback to prop
  const displayLicense = fetchedApplication && Object.keys(fetchedApplication).length > 0
    ? fetchedApplication
    : license;

  const { createLicenseApplicationDocument, creating: uploadingDocs } = useCreateLicenseApplicationDocument(
    (response: any) => {
      toast.success(`Documents submitted successfully`, {
        icon: <Icon name="snackbarSuccess" />,
      });
      setUploadedFiles({});
      refetchUploadedDocs();
      setIsSubmitting(false);
      closePanel();
    },
    (error: any) => {
      console.error("Batch upload error:", error);
      const errorData = error && error.response && error.response.data;
      const errorMessages = errorData && errorData.error_messages;

      refetchUploadedDocs();

      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        errorMessages.forEach((errMsg: string) => {
          toast.error(errMsg, { icon: <Icon name="snackbarError" /> });
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("File upload failed, try again");
      }
      setIsSubmitting(false);
    }
  );

  const allStatuses = [
    "not_started",
    "docs_pending",
    "commercials_pending",
    "pending_at_authority",
    "application_approved",
    "application_rejected",
  ];

  const { updateApplication, updating } = useUpdateLicenseApplication(
    (response: any) => {
      toast.success("License application updated successfully", {
        icon: <Icon name="snackbarSuccess" />,
      });
      setLastUpdatedStatus(selectedStatus);
      closePopup();
      closeEditPopup();
      closePanel();
      queryClient.invalidateQueries(['companyLicenses']);
      queryClient.invalidateQueries(['companyLicenseDocuments']);
      queryClient.invalidateQueries(['licenseApplications']);
      if (refetch) refetch();
    },
    (error: any) => {
      closePopup();
      closeEditPopup();
      closePanel();
      const errorData = error && error.response && error.response.data;
      const errors = errorData && errorData.errors;

      if (errors && Array.isArray(errors) && errors.length > 0) {
        errors.forEach((errMsg: string) => {
          toast.error(errMsg, {
            icon: <Icon name="snackbarError" />,
          });
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message, {
          icon: <Icon name="snackbarError" />,
        });
      } else {
        toast.error("Failed to update license application", {
          icon: <Icon name="snackbarError" />,
        });
      }
    }
  );

  useEffect(() => {
    if (
      selectedStatus === "application_approved" &&
      issuedDate &&
      displayLicense &&
      displayLicense.license_type &&
      displayLicense.license_type.default_validity_days
    ) {
      const isPerpetualAllowed = displayLicense.license_type.perpetual_allowed;
      const applicationType = displayLicense.application_type && displayLicense.application_type.action_type;
      const isSurrender = applicationType && applicationType.toLowerCase() === 'surrender';
      const isAmendment = applicationType && applicationType.toLowerCase() === 'amendment';

      // Only auto-calculate for non-perpetual, non-surrender, non-amendment licenses
      if (!isPerpetualAllowed && !isSurrender && !isAmendment) {
        const issueDate = new Date(issuedDate);
        const validityDays = displayLicense.license_type.default_validity_days;
        const calculatedExpiry = new Date(issueDate);
        calculatedExpiry.setDate(calculatedExpiry.getDate() + validityDays);

        const formattedExpiry = calculatedExpiry.toISOString().split('T')[0];
        setExpiryDate(formattedExpiry);
      } else {
        setExpiryDate("");
      }
    }
  }, [
    issuedDate,
    selectedStatus,
    displayLicense && displayLicense.license_type && displayLicense.license_type.default_validity_days,
    displayLicense && displayLicense.license_type && displayLicense.license_type.perpetual_allowed,
    displayLicense && displayLicense.application_type && displayLicense.application_type.action_type
  ]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab("details");
    }
  }, [isOpen]);

  // Scroll to top when tab changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  useEffect(() => {
    if (displayLicense && displayLicense.status) {
      setSelectedStatus(displayLicense.status);
      setLastUpdatedStatus(displayLicense.status);
    }
  }, [displayLicense]);


  useEffect(() => {
    if (lastUpdatedStatus !== null && selectedStatus === lastUpdatedStatus) {
      setSelectedStatus(lastUpdatedStatus);
    }
  }, [lastUpdatedStatus]);


  // Fetch required documents based on application type
  const {
    applicationTypeDocuments: requiredDocuments,
  } = useGetApplicationTypeDocuments(
    displayLicense && displayLicense.application_type_id ? displayLicense.application_type_id : undefined,
    displayLicense && displayLicense.application_type ? displayLicense.application_type.action_type : undefined,
    !!(displayLicense && displayLicense.application_type_id)
  );

  const {
    licenseApplicationDocuments: uploadedDocuments,
    refetch: refetchUploadedDocs,
  } = useGetLicenseApplicationDocuments(
    displayLicense && displayLicense.id ? displayLicense.id : undefined,
    !!(displayLicense && displayLicense.id)
  );

  const {
    historyItems,
  } = useGetLicenseApplicationHistory(
    displayLicense && displayLicense.id ? displayLicense.id : undefined,
    "watcher_ids,created_by,updated_by,editor_ids,assigned_to_id,escalation_user_id,status,description,license_name",
    !!(displayLicense && displayLicense.id)
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const validateFile = (
    file: File,
    allowedTypes: string[],
    maxSizeBytes: number,
    documentName: string
  ): boolean => {

    const fileExtension = (file.name.split('.').pop() || '').toLowerCase();

    if (!fileExtension || !allowedTypes.includes(fileExtension)) {
      toast.error(
        `Invalid file type. Allowed formats: ${allowedTypes.join(', ').toUpperCase()}`
      );
      return false;
    }

    if (file.size > maxSizeBytes) {
      toast.error(
        `File size exceeds limit. Maximum: 25MB.`
      );
      return false;
    }
    return true;
  };

  const openPopup = (key: string) => {
    let popup = buttonPopups[key];

    if (key === 'application_approved' && displayLicense && displayLicense.application_type) {
      const actionType = displayLicense.application_type.action_type;

      popup = {
        ...popup,
        title: actionType === 'new' ? 'New License Application Approved' :
               actionType === 'renewal' ? 'Renewal Application Approved' :
               actionType === 'amendment' ? 'Amendment Application Approved' :
               actionType === 'surrender' ? 'Surrender Application Approved' :
               'Upload Confirmation',
        description: actionType === 'new' ?
          "Great! It seems that your application for new license is approved. Please enter the following information to update the license details." :
          actionType === 'renewal' ?
          "Great! It seems that your application for renewal is approved. Please enter the following information to update the license details. " :
          actionType === 'amendment' ?
          "Great! It seems that your application for amendment is approved. Please enter the following information to update the license details. " :
          actionType === 'surrender' ?
          "Great! It seems that your application for Surrender is approved. Please enter the following information to update the license details. " :
          "You have selected the status 'application approved', in order to proceed Please upload the license details and submit",
      };
    }

    if (popup) {
      setPopupData(popup);
      setIsOpen(true);
    } else {
      console.warn("No popup available for key:", key);
    }
  };

  const closePopup = () => {
    setIsOpen(false);
    setRejectionReason("");
    setIsPerpetual(false);
    setIssuedDate("");
    setExpiryDate("");
    setUploadedFile(null);
  };

  const getUploadedDocument = (applicationTypeDocumentId: string) => {
    const doc = uploadedDocuments.find(
      (doc: any) =>
        doc.application_type_document_id === applicationTypeDocumentId
    );

    return doc;
  };

  const licenseDetails = displayLicense ? [
    { label: "ID", value: displayLicense.id || "-" },
    { label: "License Name", value: displayLicense.license_name || "-" },
    {
      label: "License Type",
      value: (displayLicense.license_type && displayLicense.license_type.name) || "-",
    },
    {
      label: "Application Type",
      value:
        displayLicense.application_type && displayLicense.application_type.action_type
          ? displayLicense.application_type.action_type
              .replace(/_/g, " ")
          : "-",
    },
    {
      label: "Company",
      value: (displayLicense.company && displayLicense.company.name) || "-",
    },
    {
      label: "Associate Company",
      value:
        (displayLicense.associate_company && displayLicense.associate_company.name) ||
        "-",
    },
    {
      label: "Location",
      value: displayLicense.location  ? `${displayLicense.location.name} - ${displayLicense.location.code}` : "-",
    },
    { label: "State", value: (displayLicense.state && displayLicense.state.name) || "-" },
    { label: "City", value: (displayLicense.city && displayLicense.city.name) || "-" },
    { label: "Authority", value: displayLicense.authority || "-" },
    { label: "Issued Date", value: formatDateDisplay(displayLicense.issued_date) },
    { label: "Expiry Date", value: (displayLicense.license_type && displayLicense.license_type.perpetual_allowed) ? "Perpetual License" : formatDateDisplay(displayLicense.expiry_date) },
    { label: "Assigned To", value: (displayLicense.assigned_to && displayLicense.assigned_to.name) || "-",
    },
    { label: "Watchers", value: displayLicense.watcher_names && displayLicense.watcher_names.length > 0  ? displayLicense.watcher_names.join(", ") : "No watchers",},
    { label: "Requested By", value: (displayLicense.requested_by && displayLicense.requested_by.name) || "-",},
  ] : [];

  const formatDateTime = (isoString: string | undefined | null) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatFieldName = (field: string): string => {
    return field
      .replace(/_/g, ' ')
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatFieldValue = (value: any): string => {
    if (value === null || value === undefined) return "(empty)";
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string') {
      if (value.trim() === '') return "(empty)";
      return value;
    }

    if (typeof value === 'object' && value.id && value.name) {
      return value.name;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return "None";
      return value.map(item => {
        if (typeof item === 'object' && item.name) {
          return item.name;
        }
        return String(item);
      }).join(', ');
    }

    return String(value);
  };

  const displayHistoryItems: DisplayHistoryItem[] = [];
  const formatStatusText = (value: string | null | undefined) => {
    if (!value) return "-";
    return value
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  if (license) {
    // Process audit logs (historyItems)
    if (historyItems && historyItems.length > 0) {
      historyItems.forEach((historyItem: HistoryItem, index: number) => {
        if (historyItem.changes && Object.keys(historyItem.changes).length > 0) {

          // Check if this audit log contains a status change
          if (historyItem.changes.status) {
            const statusChange = historyItem.changes.status;
            const fromStatus = formatStatusText(statusChange.from);
            const toStatus = formatStatusText(statusChange.to);
            const changedBy = historyItem.changed_by || '-';

            displayHistoryItems.push({
              id: 1000 + index,
              title: "Status changes",
              date: formatDateTime(historyItem.changed_at),
              rawDate: historyItem.changed_at,
              name: `Status changed from <strong>${fromStatus}</strong> to <strong>${toStatus}</strong> by <strong style="color:#2965AD" class="text-capitalize">${changedBy}</strong>`,
              description: "",
            });
          }

          // Check for other field changes (non-status fields)
          const otherFields = Object.keys(historyItem.changes).filter(field => field !== 'status');

          if (otherFields.length > 0) {
            const changeTexts: string[] = [];

            otherFields.forEach((field: string) => {
              const change = historyItem.changes[field];
              const fieldLabel = formatFieldName(field);
              const fromValue = formatFieldValue(change.from);
              const toValue = formatFieldValue(change.to);
              changeTexts.push(
                `${fieldLabel} changed from <strong>${fromValue}</strong> to <strong>${toValue}</strong> by <strong style="color:#2965AD" class="text-capitalize">${historyItem.changed_by || '-'}</strong>`
              );
            });

            const editedByText = changeTexts.length > 0 ? changeTexts.join('<br/>') : "-";

            displayHistoryItems.push({
              id: 2000 + index,
              title: "License Application Edited",
              date: formatDateTime(historyItem.changed_at),
              rawDate: historyItem.changed_at,
              name: editedByText,
              description: "",
            });
          }
        }
      });
    }

    // License Created On (always at the end chronologically, but will be sorted)
    displayHistoryItems.push({
      id: 3000,
      title: "License Created On",
      date: formatDateTime(displayLicense.created_at),
      rawDate: displayLicense.created_at,
      name: `Created By <strong style="color:#2965AD" class="text-capitalize">${displayLicense.requested_by && displayLicense.requested_by.name || "-"}</strong>`,
      description: "",
    });
  }

  // Sort by date (newest first)
  displayHistoryItems.sort((a, b) => {
    const dateA = new Date(a.rawDate || 0).getTime();
    const dateB = new Date(b.rawDate || 0).getTime();
    return dateB - dateA;
  });


  const buttonPopups: { [key: string]: PopupData } = {
    docs_pending: {
      title: "Upload Confirmation",
      uploadBoxText: "Upload files",
      description: "You have selected the status ‘Document under verification’ in order to proceed Please upload the Verification Successful confirmation message from team.",
    },
    commercials_pending: {
      title: "Upload Confirmation",
      uploadBoxText: "Upload files",
      description: "You have selected the status ‘Commercials Pending’, in order to proceed Please upload the Commercials Pending document from team.",
    },
    pending_at_authority: {
      title: "Upload Confirmation",
      uploadBoxText: "Upload files",
      description: "You have selected the status ‘Pending at authority’, in order to proceed Please upload the Verification Successful confirmation message from team.",
    },
    application_approved: {
      title: "Upload Confirmation",
      uploadBoxText: "Upload License Copy",
      description: "You have selected the status ‘application approved’, in order to proceed Please upload the license details and submit",
    },
    application_rejected: {
      title: "Confirm Rejection",
      uploadBoxText: "Upload files",
      description: "Email will be sent to all the people with this status and you will no longer be able to make any edits to this application",
    },
  };

  const [uploadedFiles, setUploadedFiles] = useState<
    Record<string, UploadedFile | null>
  >({});

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Filter documents by action_type on frontend
  const filteredDocuments = requiredDocuments || [];

  const defaultAllowedFileTypes = [
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg',
    'pdf', 'ppt', 'pptx', 'doc', 'docx'
  ];

  const documentFields = filteredDocuments.map((doc: any) => ({
    key: doc.id,
    label: doc.document_type.name || "Unknown Document",
    applicationTypeDocumentId: doc.id,
    documentTypeCode: doc.document_type.code,
    allowedFileTypes: doc.document_type.allowed_file_types && doc.document_type.allowed_file_types.length > 0
      ? doc.document_type.allowed_file_types
      : defaultAllowedFileTypes,
    maxSizeBytes: 26214400,
    mandatory: doc.mandatory,
    documentTypeId: doc.document_type.id
  }));

  useEffect(() => {
    if (documentFields.length > 0) {
      setUploadedFiles((prev) => {
        const initialState: Record<string, UploadedFile | null> = {};
        documentFields.forEach((field: any) => {
          initialState[field.key] = prev[field.key] || null;
        });
        return initialState;
      });
    }
  }, [JSON.stringify(documentFields)]);

  const totalFiles = documentFields.length;
  // Count documents that are either locally selected OR already uploaded to backend
  const uploadedCount = documentFields.filter((docField: any) => {
    const hasLocalFile = uploadedFiles[docField.key] !== null && uploadedFiles[docField.key] !== undefined;
    const hasBackendFile = getUploadedDocument(docField.applicationTypeDocumentId);
    return hasLocalFile || hasBackendFile;
  }).length;
  const uploadProgress = totalFiles > 0 ? Math.round((uploadedCount / totalFiles) * 100) : 0;

  const checkMandatoryDocumentsSubmitted = () => {
    const missingMandatory = documentFields.filter((docField: any) => {
      if (!docField.mandatory) return false;
      const uploadedDoc = getUploadedDocument(docField.applicationTypeDocumentId);
      return !uploadedDoc;
    });
    return missingMandatory.length === 0;
  };
  useEffect(() => {
    const allSubmitted = checkMandatoryDocumentsSubmitted();
    setAllMandatoryDocsSubmitted(allSubmitted);
  }, [uploadedDocuments, documentFields]);

  const handleChooseFile = (key: string) => {
    const inputElement = fileInputRefs.current[key];
    if (inputElement) {
      inputElement.click();
    }
  };

  const handleFileSelect = async (
    key: string,
    e: React.ChangeEvent<HTMLInputElement>,
    docField: any
  ) => {
    const files = e.target && e.target.files ? e.target.files : [];
    const file = files.length > 0 ? files[0] : null;
    if (file) {

      // Validate file
      const isValid = validateFile(
        file,
        docField.allowedFileTypes,
        docField.maxSizeBytes,
        docField.label
      );

      if (!isValid) {
        // Reset the input
        e.target.value = '';
        return;
      }

      const fileURL = URL.createObjectURL(file);
      setUploadedFiles((prev) => ({
        ...prev,
        [key]: { file, url: fileURL, uploading: true },
      }));

      // Show page loader
      setIsUploadingFile(true);

      try {
        const uploadFormData = new FormData();
        uploadFormData.append("file", file);
        uploadFormData.append("resource_type", "license_application");
        uploadFormData.append("file_type", "license_application/document");
        uploadFormData.append("resource_field", "license_application_document");
        uploadFormData.append("skip_deep_scan", "true");

        const response = await post(
          "/api/v1/upload",
          uploadFormData,
          { "Content-Type": "multipart/form-data" },
          true
        );

        const responseData = response && response.data;
        if (responseData && responseData.url) {
          const fileInfo = responseData.file_info || {};
          const uploadedUrl = responseData.url;

          let s3Key = "";
          let s3Bucket = "";
          let s3Region = "";

          const s3Params = parseS3Url(uploadedUrl);
          if (s3Params) {
            s3Bucket = s3Params.s3_bucket;
            s3Region = s3Params.s3_region;
            s3Key = s3Params.s3_key;
          }

          setUploadedFiles((prev) => ({
            ...prev,
            [key]: {
              file,
              url: fileURL,
              uploaded_url: uploadedUrl,
              uploading: false,
              file_name: fileInfo.filename || file.name,
              content_type: fileInfo.content_type || file.type,
              file_size: fileInfo.size || file.size,
              s3_key: s3Key,
              s3_bucket: s3Bucket,
              s3_region: s3Region,
            },
          }));
          toast.success(`${docField.label} uploaded successfully`);
        }
      } catch (error: any) {
        const errorMessage = error && error.response && error.response.data && error.response.data.details
          ? error.response.data.details.join(", ")
          : "Failed to upload file";
        toast.error(`${errorMessage}: ${file.name}`);

        setUploadedFiles((prev) => ({
          ...prev,
          [key]: null,
        }));

        e.target.value = '';
      } finally {
        setIsUploadingFile(false);
      }
    }
  };

  const handleClosePanel = () => {
    setActiveTab("details");
    setLastUpdatedStatus(null);
    closePanel();
  };

  const handleRemoveFile = (key: string) => {
    const inputElement = fileInputRefs.current[key];
    if (inputElement) {
      inputElement.value = '';
    }

    setUploadedFiles((prev) => ({
      ...prev,
      [key]: null,
    }));
  };

  const handleDownload = (fileData: UploadedFile) => {
    const link = document.createElement("a");
    link.href = fileData.url;
    link.download = fileData.file.name;
    link.click();
  };

  const handleBatchSubmit = async () => {
    setIsSubmitting(true);

    const selectedFiles = documentFields.filter((docField: any) =>
      uploadedFiles[docField.key] !== null && uploadedFiles[docField.key] !== undefined
    );

    if (selectedFiles.length === 0) {
      toast.error(`Please upload all mandatory documents`);
      setIsSubmitting(false);
      return;
    }

    const missingMandatory = documentFields.filter((docField: any) => {
      if (!docField.mandatory) return false;
      const hasLocalFile = uploadedFiles[docField.key] !== null && uploadedFiles[docField.key] !== undefined;
      const hasBackendFile = getUploadedDocument(docField.applicationTypeDocumentId);
      return !hasLocalFile && !hasBackendFile;
    });

    if (missingMandatory.length > 0) {
      toast.error(`Please upload all mandatory documents`);
      setIsSubmitting(false);
      return;
    }
    const isUploading = selectedFiles.some((docField: any) => {
      const fileData = uploadedFiles[docField.key];
      return fileData && fileData.uploading;
    });

    if (isUploading) {
      toast.error("Please wait for all files to finish uploading");
      setIsSubmitting(false);
      return;
    }

    const missingUrls = selectedFiles.filter((docField: any) => {
      const fileData = uploadedFiles[docField.key];
      return fileData && !fileData.uploaded_url;
    });

    if (missingUrls.length > 0) {
      toast.error("Some files failed to upload. Please try again.");
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((docField: any, index: number) => {
      const fileData = uploadedFiles[docField.key];
      if (fileData && fileData.uploaded_url) {
        formData.append(
          `license_application_document[${index}][file_url]`,
          fileData.uploaded_url
        );
        formData.append(
          `license_application_document[${index}][s3_bucket]`,
          fileData.s3_bucket || ""
        );
        formData.append(
          `license_application_document[${index}][s3_region]`,
          fileData.s3_region || ""
        );
        formData.append(
          `license_application_document[${index}][s3_key]`,
          fileData.s3_key || ""
        );
        formData.append(
          `license_application_document[${index}][filename]`,
          fileData.file_name || fileData.file.name
        );
        formData.append(
          `license_application_document[${index}][content_type]`,
          fileData.content_type || fileData.file.type
        );
        formData.append(
          `license_application_document[${index}][file_size]`,
          (fileData.file_size || fileData.file.size).toString()
        );
        formData.append(
          `license_application_document[${index}][application_type_document_id]`,
          docField.applicationTypeDocumentId
        );
        formData.append(
          `license_application_document[${index}][license_application_id]`,
          displayLicense.id || ""
        );
      }
    });

    // Make the API call
    createLicenseApplicationDocument(formData);
  };

  const openEditPopup = () => setIsEditOpen(true);
  const closeEditPopup = () => setIsEditOpen(false);

  const handleCostDetailsUpdate = async (_costTypeKey: string, data: CostEditFormData, originalData?: CostEditFormData): Promise<void> => {
    if (!displayLicense || !displayLicense.id || !data.id) return;

    const costDetailUpdate: Record<string, any> = {
      id: data.id,
    };

    // Compare and add only changed fields
    if (!originalData || data.cost_description !== originalData.cost_description) {
      costDetailUpdate.cost_description = data.cost_description;
    }
    if (!originalData || data.cost_amount !== originalData.cost_amount) {
      costDetailUpdate.cost_amount = parseFloat(data.cost_amount) || 0;
    }
    if (!originalData || data.tax_applicable !== originalData.tax_applicable) {
      costDetailUpdate.tax_applicable = data.tax_applicable;
    }
    if (!originalData || data.tax_amount !== originalData.tax_amount) {
      costDetailUpdate.tax_amount = data.tax_applicable ? (parseFloat(data.tax_amount) || 0) : 0;
    }
    if (!originalData || data.total_cost_including_tax !== originalData.total_cost_including_tax) {
      costDetailUpdate.total_cost = parseFloat(data.total_cost_including_tax) || 0;
    }
    if (!originalData || data.vendor_service_provider_name !== originalData.vendor_service_provider_name) {
      costDetailUpdate.vendor_name = data.vendor_service_provider_name;
    }
    if (!originalData || data.payment_status !== originalData.payment_status) {
      costDetailUpdate.payment_status = data.payment_status;
    }
    if (!originalData || data.cost_period_from !== originalData.cost_period_from) {
      costDetailUpdate.cost_period_from = data.cost_period_from;
    }
    if (!originalData || data.cost_period_to !== originalData.cost_period_to) {
      costDetailUpdate.cost_period_to = data.cost_period_to;
    }

    const payload = {
      id: displayLicense.id,
      license_application: {
        cost_details_attributes: [costDetailUpdate],
      },
    };

    return new Promise((resolve, reject) => {
      updateApplication(payload as any, {
        onSuccess: () => resolve(),
        onError: (error: any) => reject(error),
      });
    });
  };

  const handleSave = (data: any, section: string) => {
    if (!displayLicense || !displayLicense.id) return;

    const payload = {
      id: displayLicense.id,
      license_application: {
        ...data,
        license_name: displayLicense.license_name,
        updated_by_id: user.user_id,
      },
    };

    updateApplication(payload as any);
  };

  const handleStatusUpdate = () => {
    if (!displayLicense || !displayLicense.id) {
      toast.error("License information is missing");
      return;
    }

    if (selectedStatus === "application_rejected") {
      if (!rejectionReason.trim()) {
        toast.error("Please provide a rejection reason");
        return;
      }
    }

    if (selectedStatus === "application_approved") {

      const licenseType = displayLicense.license_type;
      const isPerpetualAllowed = licenseType && licenseType.perpetual_allowed;
      const applicationType = displayLicense.application_type && displayLicense.application_type.action_type;
      const isSurrender = applicationType && applicationType.toLowerCase() === 'surrender';
      const isAmendment = applicationType && applicationType.toLowerCase() === 'amendment';

      if (isSurrender || isAmendment) {

        if (!isPerpetualAllowed) {
          const referenceExpiryDate =
            (displayLicense.license_reference && displayLicense.license_reference.expiry_date) ||
            (displayLicense.company_license && displayLicense.company_license.expiry_date) ||
            displayLicense.expiry_date;

          if (!referenceExpiryDate) {
            toast.error("Cannot approve: Expiry date not found in the original license.");
            return;
          }
        }
      } else {
        if (!issuedDate) {
          toast.error("Please provide issued date");
          return;
        }

        if (!isPerpetualAllowed && !expiryDate) {
          toast.error("Expiry date could not be calculated. Please ensure issued date is provided.");
          return;
        }
        if (!isPerpetualAllowed && expiryDate) {
          const expiryDateObj = new Date(expiryDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (expiryDateObj < today) {
            toast.error("Cannot issue license: The calculated expiry date is in the past. Please check the issued date.");
            return;
          }
        }
      }

      if (!uploadedFile) {
        toast.error("Please upload license file");
        return;
      }
    }

    if (["docs_pending", "commercials_pending", "pending_at_authority"].includes(selectedStatus)) {
      if (!uploadedFile) {
        toast.warning("Uploading a document is recommended for this status");
      }
    }

    const needsFile = uploadedFile !== null;

    let payload: any;

    if (needsFile) {
      const formData = new FormData();

      formData.append('license_application[status]', selectedStatus);
      formData.append('license_application[updated_by_id]', user.user_id);

      if (selectedStatus === "application_rejected") {
        formData.append('license_application[rejection_reason]', rejectionReason);
      }

      if (selectedStatus === "application_approved") {
        const licenseType = displayLicense.license_type;
        const isPerpetualAllowed = licenseType && licenseType.perpetual_allowed;
        const applicationType = displayLicense.application_type && displayLicense.application_type.action_type;
        const isSurrender = applicationType && applicationType.toLowerCase() === 'surrender';
        const isAmendment = applicationType && applicationType.toLowerCase() === 'amendment';

        if (isSurrender || isAmendment) {
          const referenceIssueDate =
            (displayLicense.license_reference && displayLicense.license_reference.issued_date) ||
            (displayLicense.company_license && displayLicense.company_license.issued_date) ||
            displayLicense.issued_date;
          formData.append('license_application[issued_date]', referenceIssueDate);

          const isReferencePerpetual =
            (displayLicense.license_reference && displayLicense.license_reference.is_perpetual) ||
            (displayLicense.company_license && displayLicense.company_license.is_perpetual);

          if (!isPerpetualAllowed && !isReferencePerpetual) {
            const referenceExpiryDate =
              (displayLicense.license_reference && displayLicense.license_reference.expiry_date) ||
              (displayLicense.company_license && displayLicense.company_license.expiry_date) ||
              displayLicense.expiry_date;
            formData.append('license_application[expiry_date]', referenceExpiryDate);
          }
        } else {
          formData.append('license_application[issued_date]', issuedDate);

          if (!isPerpetualAllowed && expiryDate) {
            formData.append('license_application[expiry_date]', expiryDate);
          }
        }
      }

      formData.append('license_application[license_file]', uploadedFile);

      payload = {
        id: displayLicense.id,
        formData: formData,
        isFileUpload: true
      };

    } else {
      payload = {
        id: displayLicense.id,
        license_application: {
          status: selectedStatus,
          updated_by_id: user.user_id,
        },
      };

      if (selectedStatus === "application_rejected") {
        payload.license_application.rejection_reason = rejectionReason;
      }

      if (selectedStatus === "application_approved") {
        const licenseType = displayLicense.license_type;
        const isPerpetualAllowed = licenseType && licenseType.perpetual_allowed;
        const applicationType = displayLicense.application_type && displayLicense.application_type.action_type;
        const isSurrender = applicationType && applicationType.toLowerCase() === 'surrender';
        const isAmendment = applicationType && applicationType.toLowerCase() === 'amendment';

        if (isSurrender || isAmendment) {
          const referenceIssueDate =
            (displayLicense.license_reference && displayLicense.license_reference.issued_date) ||
            (displayLicense.company_license && displayLicense.company_license.issued_date) ||
            displayLicense.issued_date;

          payload.license_application.issued_date = referenceIssueDate;

        if (!isPerpetualAllowed) {
            const referenceExpiryDate =
              (displayLicense.license_reference && displayLicense.license_reference.expiry_date) ||
              (displayLicense.company_license && displayLicense.company_license.expiry_date) ||
              displayLicense.expiry_date;

            payload.license_application.expiry_date = referenceExpiryDate;
          }
        } else {
          payload.license_application.issued_date = issuedDate;
          if (!isPerpetualAllowed && expiryDate) {
            payload.license_application.expiry_date = expiryDate;
          }
        }
      }

    }

    updateApplication(payload);
  };

  const handleDirectStatusUpdate = () => {
    if (!displayLicense || !displayLicense.id) {
      toast.error("License information is missing");
      return;
    }

    const payload = {
      id: displayLicense.id,
      license_application: {
        status: selectedStatus,
        updated_by_id: user.user_id,
      },
    };

    updateApplication(payload);
  };

  const getStatusColorClass = (status:any) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes("not_started")) return "not_started";
    if (statusLower.includes("docs_pending")) return "renewal";
    if (statusLower.includes("rejected")) return "expired";
    if (statusLower.includes("expired")) return "expired";
    if (statusLower.includes("application_approved")) return "active";
    if (statusLower.includes("pending_at_authority")) return "renewal";
    if (statusLower.includes("commercials_pending")) return "renewal";
    return "";
  };

  return (
    <>
      {isOpen && (
        <div
          className="licensedetails-overlay"
          onClick={isSubmitting ? undefined : handleClosePanel}
          style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
        ></div>
      )}
      <div className="licensedetails-wrapper">
        <div className={`licensedetails ${isOpen ? "open" : ""}`}  style={{height: "calc(100% - 151px)"}} ref={scrollContainerRef}>
          <div className="licensedetails-header pb-0">
            <div className="d-flex justify-content-between align-items-start">
              <h5 className="mb-0 fw-semibold">Application Details</h5>
              <div className="d-flex justify-content-between align-items-center gap-4">
                {hasUserAccess(
                  USER_PRIVILEGES.CREATE_AND_EDIT_LICENSE_APPLICATION
                ) && (
                  <div
                    className={`d-flex align-items-center gap-2 ${
                      (displayLicense && displayLicense.status === 'application_approved') ||
                      (displayLicense && displayLicense.status === 'application_rejected')
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}
                    onClick={(e) => {
                      if (
                        (displayLicense && displayLicense.status === 'application_approved') ||
                        (displayLicense && displayLicense.status === 'application_rejected')
                      ) {
                        e.preventDefault();
                        return;
                      }
                      openEditPopup();
                    }}
                  >
                    <img src={editIcon} alt="editIcon" />
                    <span className="edit_button">Edit</span>
                  </div>
                )}
                <button
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleClosePanel}
                  disabled={isSubmitting}
                ></button>
              </div>
            </div>

            <div>
              <ul className="progress-section p-0 m-0 mt-4 pl-2">
                <li
                  className={activeTab === "details" ? "active" : ""}
                  onClick={() => setActiveTab("details")}
                >
                  Details
                </li>
                {(documentFields.length > 0) && (
                <li
                  className={activeTab === "documents" ? "active" : ""}
                  onClick={() => setActiveTab("documents")}
                >
                  Documents
                </li>)}
                <li
                  className={activeTab === "history" ? "active" : ""}
                  onClick={() => setActiveTab("history")}
                >
                  History
                </li>
              </ul>
            </div>
          </div>
          <div className="licensedetails-body h-100">            
            {/* License Details Tab */}
            {activeTab === "details" && (
              <>
                <ul className="licensedetails-text pb-1">
                {licenseDetails.map((item, index) => (
                  <li key={index}>
                    <div>{item.label}</div>
                    <div>{item.value}</div>
                  </li>
                ))}
                {displayLicense && displayLicense.application_type && displayLicense.application_type.sop && (
                  <li>
                    <div>
                      <label className="mb-2 fw-semibold">
                        Standard Operating Procedure for {displayLicense.application_type.action_type === 'new' ? 'New' :
                          displayLicense.application_type.action_type === 'renewal' ? 'Renewal' :
                          displayLicense.application_type.action_type === 'amendment' ? 'Amendment' :
                          displayLicense.application_type.action_type === 'surrender' ? 'Surrender' :
                          displayLicense.application_type.action_type}
                      </label>
                    </div>
                    <div className='w-100'>
                      <div className='w-100 licensetypedetails-textarea' style={{ whiteSpace: 'pre-wrap' }}>
                        {displayLicense.application_type.sop || 'No Standard Operating Procedure provided'}
                      </div>
                    </div>
                  </li>
                )}
                <li>
                  <div>License Application Status</div>

                  {/* If user has access → show SELECT */}
                  {hasUserAccess(USER_PRIVILEGES.CREATE_AND_EDIT_LICENSE_APPLICATION) ? (
                    <div>
                      <select
                        className="licensedetails-select form-select"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        style={{ width: "240px" }}
                        disabled={
                          (displayLicense && displayLicense.status === "application_approved") ||
                          (displayLicense && displayLicense.status === "application_rejected") ||
                          (displayLicense &&
                            displayLicense.status === "docs_pending" &&
                            !allMandatoryDocsSubmitted)
                        }
                      >
                        <option value="not_started" disabled={lastUpdatedStatus === "not_started"}>Not Started</option>
                        <option value="docs_pending" disabled={lastUpdatedStatus === "docs_pending"}>Docs Pending</option>
                        <option value="commercials_pending" disabled={lastUpdatedStatus === "commercials_pending"}>Commercials Pending</option>
                        <option value="pending_at_authority" disabled={lastUpdatedStatus === "pending_at_authority"}>Pending at Authority</option>
                        <option value="application_approved">Application Approved</option>
                        <option value="application_rejected">Application Rejected</option>
                      </select>

                      {displayLicense &&
                        displayLicense.status === "docs_pending" &&
                        !allMandatoryDocsSubmitted && (
                          <div className="text-muted small mt-1">
                            Please submit all the documents in the "Documents" tab before going ahead with this application. 
                          </div>
                        )}
                    </div>
                  ) : (
                    // If no access → show colored status text
                    <span
                      className={`text-capitalize license-tag color-class fw-bold ${getStatusColorClass(
                        displayLicense && displayLicense.status || ""
                      )}`}
                    >
                      {displayLicense && displayLicense.status.replace(/_/g, " ")}
                    </span>
                  )}
                </li>
                {(displayLicense && displayLicense.rejection_reason) && 
                  <li>
                    <div></div>
                    <div className="text-capitalize text-md">
                      <span className="d-block" style={{color: "#374151", fontWeight: "400"}}>Reason:</span><div className="fw-medium">{displayLicense && displayLicense.rejection_reason || "-"}</div></div>
                  </li>
                }
              </ul>

              {/* Cost Details Accordion */}
              {displayLicense && displayLicense.cost_details && displayLicense.cost_details.length > 0 && (
                <CostDetailsAccordion
                  costDetails={displayLicense.cost_details}
                  licenseId={displayLicense.id}
                  onUpdate={handleCostDetailsUpdate}
                  canEdit={
                    (hasUserAccess(USER_PRIVILEGES.CREATE_AND_EDIT_LICENSE_APPLICATION) || (hasUserAccess(USER_PRIVILEGES.EDIT_COST_DETAILS))) &&
                    displayLicense.status !== "application_approved" &&
                    displayLicense.status !== "application_rejected"
                  }
                  isLoading={updating}
                />
              )}
              </>
            )}

            {activeTab === "documents" && (
              <>
                {(getUserRole() !== "License Watcher") && !((displayLicense && displayLicense.status === 'application_approved') || (displayLicense && displayLicense.status === 'application_rejected')) &&
                  <div className="progress-container">
                    <h2>{uploadProgress}%</h2>
                    <p>
                      {uploadProgress === 100
                        ? "All required documents have been uploaded"
                        : `${uploadedCount} of ${totalFiles} documents uploaded. Upload the remaining documents to proceed with the application`}
                    </p>
                    <div className="progress-bar">
                      <div
                        style={{
                          width: `${uploadProgress}%`,
                          background: "var(--royal-blue)",
                          height: "8px",
                          transition: "width 0.4s ease"
                        }}
                      ></div>
                    </div>
                  </div>
                }

                <div className="document-container pb-3 mt-4">
                  {(() => {
                    const isApprovedOrRejected =
                      (displayLicense && displayLicense.status === 'application_approved') ||
                      (displayLicense && displayLicense.status === 'application_rejected');
                    const isLicenseWatcher = getUserRole() === "License Watcher";

                    // Filter documents to show
                    const docsToShow = documentFields.filter((doc: any) => {
                      const uploadedDoc = getUploadedDocument(doc.applicationTypeDocumentId);

                      if (isLicenseWatcher && !uploadedDoc) {
                        return false;
                      }

                      if (isApprovedOrRejected && !uploadedDoc) {
                        return false;
                      }

                      return true;
                    });

                    // Show "No documents" message if no documents to display
                    if (docsToShow.length === 0) {
                      return (
                        <div className="text-center text-muted py-4">
                          No documents Submitted for this License Application
                        </div>
                      );
                    }

                    // Render documents
                    return docsToShow.map((doc: any) => {
                      const fileData = uploadedFiles[doc.key];
                      const uploadedDoc = getUploadedDocument(doc.applicationTypeDocumentId);
                      const isApprovedOrRejected =
                        (displayLicense && displayLicense.status === 'application_approved') ||
                        (displayLicense && displayLicense.status === 'application_rejected');

                      const acceptTypes = doc.allowedFileTypes.map((type: string) => `.${type}`).join(',');

                      return (
                        <div key={doc.key} className="mb-4">
                          <div className="d-flex align-items-center">
                            <label className="form-label ellipse two-lines">
                              {doc.label}
                              {doc.mandatory && <span className="text-danger ms-1">*</span>}
                            </label>

                            {!isApprovedOrRejected && !uploadedFiles[doc.key] && !uploadedDoc && (
                              <div
                                className="p-4 text-center upload-box w-100"
                                onClick={() => handleChooseFile(doc.key)}
                              >
                                <input
                                  ref={(el) => {
                                    fileInputRefs.current[doc.key] = el;
                                  }}
                                  type="file"
                                  accept={acceptTypes}
                                  onChange={(e) => handleFileSelect(doc.key, e, doc)}
                                  style={{ display: "none" }}
                                />
                                <div className="mb-2">
                                  <img src={cloudUpload} alt="cloudUpload" />
                                </div>
                                <p className="mb-2 text-md">
                                  Click to browse and upload
                                </p>
                                <small className="text-gray-12 d-block">
                                  Supported formats (Max {(doc.maxSizeBytes / 1024 / 1024).toFixed(0)}MB): {doc.allowedFileTypes.join(', ').toUpperCase()}
                                </small>
                              </div>
                            )}

                            {!isApprovedOrRejected && fileData && !uploadedDoc && (
                              <div className="upload-successful position-relative">
                                {!fileData.uploading && (
                                  <div
                                    className="close-button position-absolute"
                                    style={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveFile(doc.key);
                                    }}
                                  >
                                    <img
                                      className="d-block"
                                      src={cancelButton}
                                      alt="cancelButton"
                                    />
                                  </div>
                                )}

                                <div className="p-4 d-flex align-items-center justify-content-between">
                                  <p className="text-sm fw-semibold m-0 img_name_truncate">
                                    {fileData.file.name}
                                  </p>
                                  <div
                                    style={{
                                      cursor: "pointer",
                                      height: "25px",
                                      width: "25px",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownload(uploadedFiles[doc.key]!);
                                    }}
                                  >
                                    <img
                                      className="d-block"
                                      src={downloadButton}
                                      alt="downloadButton"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {uploadedDoc && (
                              <div className="upload-successful position-relative">
                                <div className="p-4 d-flex align-items-center justify-content-between">
                                  <p className="text-sm fw-semibold m-0 img_name_truncate">
                                    {uploadedDoc.asset.name || 'Uploaded Document'}
                                  </p>
                                  <div
                                    style={{
                                      cursor: "pointer",
                                      height: "25px",
                                      width: "25px",
                                    }}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (uploadedDoc.asset && uploadedDoc.asset.file_path) {
                                        try {
                                          const fileName = uploadedDoc.asset.name || uploadedDoc.asset.original_filename || 'document';
                                          await downloadS3FileSecurely(fileName, uploadedDoc.asset.file_path);
                                        } catch (error) {
                                          console.error("Error downloading file:", error);
                                          toast.error("Failed to download file");
                                        }
                                      }
                                    }}
                                  >
                                    <img
                                      className="d-block"
                                      src={downloadButton}
                                      alt="downloadButton"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            )}

            {/* History Tab */}
            {activeTab === "history" && (
              <>
                <div className="history-accordion">
                  {displayHistoryItems.map((item, index) => (
                    <div key={item.id} className="accordion-item">
                      <div className="accordion-timeline">
                        <div
                          className={`timeline-dot d-flex justify-content-center align-items-center ${
                            item ? "active" : ""
                          }`}
                        >
                          <div className="progress-checked"></div>{" "}
                        </div>
                        {index < displayHistoryItems.length - 1 && (
                          <div className="timeline-line">
                            <div
                              className={`dark-line ${
                                expandedId === item.id ? "expanded" : ""
                              }`}
                            ></div>
                          </div>
                        )}
                      </div>
                      <div className="accordion-content-wrapper">
                        <div
                          className="accordion-header"
                        >
                          <div className="accordion-header-left">
                            <h6
                              className="accordion-title mb-0"
                              dangerouslySetInnerHTML={{ __html: item.title }}
                            ></h6>
                           <span className="accordion-name" dangerouslySetInnerHTML={{ __html: item.name }}></span>
                            <span className="accordion-date">{item.date}</span>
                          </div>
                        </div>
                        <div
                          className={`accordion-body ${
                            expandedId === item.id ? "expanded" : "collapsed"
                          }`}
                        >
                          <div className="accordion-body-content">
                            {item.description}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className={`button-container ${isOpen ? "open" : ""}`}>
          {hasUserAccess(
            USER_PRIVILEGES.CREATE_AND_EDIT_LICENSE_APPLICATION
          ) &&
          <>
            {(activeTab === "details"
            ) && (
              <div className="d-flex gap-3 w-100">
                <button
                  type="button"
                  className={`button-primary w-100 fw-semibold ${
                    (displayLicense && displayLicense.status === 'application_approved') ||
                    (displayLicense && displayLicense.status === 'application_rejected')
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                  disabled={
                    (displayLicense && displayLicense.status === 'application_approved') ||
                    (displayLicense && displayLicense.status === 'application_rejected')
                  }
                  onClick={() => {
                    if (selectedStatus === 'application_approved' || selectedStatus === 'application_rejected') {
                      openPopup(selectedStatus);
                    }
                    else {
                      handleDirectStatusUpdate();
                    }
                  }}
                >
                  Update
                </button>
              </div>
            )}
            {activeTab === "documents" && (
              <div className="d-flex gap-3 w-100">
                {!(displayLicense && (displayLicense.status === 'application_approved' || displayLicense.status === 'application_rejected')) &&
                  Object.keys(uploadedFiles).some(key => uploadedFiles[key] !== null && uploadedFiles[key] !== undefined) && (
                  <button
                    type="button"
                    className="button-secondary w-100 fw-semibold"
                    onClick={() => {
                      const uploadingFiles = Object.keys(uploadedFiles).filter(key => {
                        const file = uploadedFiles[key];
                        return file && file.uploading;
                      });

                      if (uploadingFiles.length > 0) {
                        toast.error("Cannot remove files while uploading");
                        return;
                      }
                      const fileCount = Object.keys(uploadedFiles).filter(key =>
                        uploadedFiles[key] !== null && uploadedFiles[key] !== undefined
                      ).length;

                      if (fileCount > 0) {
                        setUploadedFiles({});
                        toast.info(`${fileCount} selected file${fileCount !== 1 ? 's' : ''} cleared`);
                      }
                    }}
                  >
                    Remove All
                  </button>
                )}
                {/* Only show Submit button if status is not approved/rejected AND there are files to submit */}
                {!(displayLicense && (displayLicense.status === 'application_approved' || displayLicense.status === 'application_rejected')) &&
                  Object.keys(uploadedFiles).some(key => uploadedFiles[key] !== null && uploadedFiles[key] !== undefined) && (
                  <button
                    type="button"
                    className="button-primary w-100 fw-semibold"
                    onClick={handleBatchSubmit}
                    disabled={isSubmitting || Object.values(uploadedFiles).some(file => file && file.uploading)}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                )}
              </div>
            )}
          </>}
        </div>

        {open && popupData && (
          <div className="license-popup-overlay">
            <div className="license-popup">
              <div className="popup-header d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="fw-semibold fs-5 m-0">{popupData.title}</h6>
                  <p>{popupData.description}</p>
                </div>
                <button
                  className="btn-close"
                  aria-label="Close"
                  onClick={closePopup}
                ></button>
              </div>
              <hr  className="mt-0"/>
              {selectedStatus === "application_rejected" ? (
                <div className="popup-body fs-6">
                  <p className="text-sm mb-2">Reason*</p>
                  <textarea
                    name="reason"
                    id="reason"
                    placeholder="Write reason here"
                    className="form-control"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  ></textarea>
                </div>
              ) : (
                <PopupUploadSection
                  selectedStatus={selectedStatus}
                  popupData={popupData}
                  isPerpetual={isPerpetual}
                  setIsPerpetual={setIsPerpetual}
                  issuedDate={issuedDate}
                  setIssuedDate={setIssuedDate}
                  expiryDate={expiryDate}
                  setExpiryDate={setExpiryDate}
                  onFileSelect={setUploadedFile}
                  applicationType={displayLicense && displayLicense.application_type && displayLicense.application_type.action_type}
                  isPerpetualAllowed={displayLicense && displayLicense.license_type && displayLicense.license_type.perpetual_allowed}
                />
              )}
              <hr />
              <div className="d-flex gap-3 w-100">
                <button
                  type="button"
                  className="button-secondary w-100 fw-semibold"
                  onClick={closePopup}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-primary w-100 fw-semibold"
                  onClick={handleStatusUpdate}
                  disabled={updating}
                >
                  {updating ? "Submitting..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        )}
        {displayLicense && (
          <EditDetails
            isOpen={isEditOpen}
            onClose={closeEditPopup}
            activeSection="license-application"
            initialData={{
              assigned_to_user_id:
                displayLicense.assigned_to_id ||
                (displayLicense.assigned_to && displayLicense.assigned_to.id) ||
                "",
              watcher_ids: displayLicense.watcher_ids || [],
              company_id: displayLicense.company_id || "",
              associate_company_id: displayLicense.associate_company_id || "",
              location_id: displayLicense.location_id || (displayLicense.location && displayLicense.location.id) || "",
              license_name: displayLicense.license_name || "",
            }}
            onSave={handleSave}
            isLoading={updating}
          />
        )}
      </div>
      {(updating || isSubmitting || isUploadingFile) && <PageLoader message={isUploadingFile ? "Please wait. File upload is in progress." : "Please wait..."} />}
    </>
  );
};

export default LicenseApplicationDetail;

const PopupUploadSection: React.FC<{
  selectedStatus: string;
  popupData: PopupData;
  isPerpetual: boolean;
  setIsPerpetual: (value: boolean) => void;
  issuedDate: string;
  setIssuedDate: (value: string) => void;
  expiryDate: string;
  setExpiryDate: (value: string) => void;
  onFileSelect: (file: File | null) => void;
  applicationType?: string;
  isPerpetualAllowed?: boolean;
}> = ({
  selectedStatus,
  popupData,
  issuedDate,
  setIssuedDate,
  expiryDate,
  onFileSelect,
  applicationType,
  isPerpetualAllowed,
}) => {
  const [popupFiles, setPopupFiles] = useState<
    Record<string, UploadedFile | null>
  >({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target && e.target.files ? e.target.files : [];
    const file = files.length > 0 ? files[0] : null;

    if (file) {
      const allowedTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'pdf', 'ppt', 'pptx', 'doc', 'docx'];
      const maxSizeBytes = 26214400; // 25MB
      const fileExtension = (file.name.split('.').pop() || '').toLowerCase();

      if (!fileExtension || !allowedTypes.includes(fileExtension)) {
        toast.error(
          `Invalid file type. Allowed formats: ${allowedTypes.join(', ').toUpperCase()}`
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      if (file.size > maxSizeBytes) {
        toast.error(
          `File size exceeds limit. Maximum: 25MB.`
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const fileURL = URL.createObjectURL(file);
      setPopupFiles((prev) => ({
        ...prev,
        [selectedStatus]: { file, url: fileURL },
      }));
      onFileSelect(file);
    }
  };

  const handleRemoveFile = () => {
    setPopupFiles((prev) => ({
      ...prev,
      [selectedStatus]: null,
    }));
    onFileSelect(null);
  };

  const currentFile = popupFiles[selectedStatus];

  return (
    <div className="popup-body fs-6">
      {selectedStatus === "application_approved" && (
        <div>

          {applicationType && applicationType.toLowerCase() !== 'surrender' && applicationType.toLowerCase() !== 'amendment' && (
            <div className="mb-3">
              <label className="text-md">Issued On*</label>
              <input
                type="date"
                className="form-control text-md"
                placeholder="-/-"
                value={issuedDate}
                onChange={(e) => setIssuedDate(e.target.value)}
              />
            </div>
          )}

          {isPerpetualAllowed !== true && applicationType && applicationType.toLowerCase() !== 'surrender' && applicationType.toLowerCase() !== 'amendment' && (
            <div className="mb-3">
              <label className="text-md">Expiry Date</label>
              <input
                type="date"
                className="form-control text-md bg-light"
                placeholder="-/-"
                value={expiryDate}
                readOnly
                disabled
                style={{cursor: 'not-allowed'}}
              />
            </div>
          )}
        </div>
      )}

      <p className="text-md mb-2">{popupData.uploadBoxText}*</p>

      {!currentFile ? (
        <div className="p-4 text-center upload-box w-100" onClick={handleChooseFile}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <div className="mb-2">
            <img src={cloudUpload} alt="cloudUpload" />
          </div>
          <p className="mb-2 text-md">
            Click to browse and upload
          </p>
          <button type="button" className="mb-2">
            Choose File
          </button>
          <div>
            <small className="text-gray-12 d-block">
              Supported formats (Upto 25 MB): JPG, PNG, WEBP, SVG, PDF, JPEG
            </small>
          </div>
        </div>
      ) : (
        <div className="upload-successful position-relative">
          <div
            className="close-button position-absolute"
            style={{ cursor: "pointer" }}
            onClick={handleRemoveFile}
          >
            <img className="d-block" src={cancelButton} alt="cancelButton" />
          </div>

          <div className="p-4 d-flex align-items-center justify-content-between">
            <p className="text-sm fw-semibold m-0 img_name_truncate">{currentFile.file.name}</p>
            <div
              style={{
                cursor: "pointer",
                height: "25px",
                width: "25px",
              }}
              onClick={() => {
                const link = document.createElement("a");
                link.href = currentFile.url;
                link.download = currentFile.file.name;
                link.click();
              }}
            >
              <img className="d-block" src={downloadButton} alt="downloadButton" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
